import { useState, useEffect } from "react";
import { apiFetch } from "../lib/api.ts";
import type { Project } from "./useProjects.ts";

interface MatchedTool {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  website: string | null;
  relevance: number;
}

interface MatchedCategory {
  id: number;
  slug: string;
  name: string;
  description: string;
  icon: string;
  relevance: number;
}

interface SearchResponse {
  data: {
    projects: Project[];
    tools: MatchedTool[];
    categories: MatchedCategory[];
  };
  meta: { query: string; mode: "semantic" | "keyword"; total_projects: number };
}

export type { MatchedTool, MatchedCategory };

export function useSearch(query: string, page: number = 1) {
  const [results, setResults] = useState<Project[]>([]);
  const [matchedTools, setMatchedTools] = useState<MatchedTool[]>([]);
  const [matchedCategories, setMatchedCategories] = useState<MatchedCategory[]>([]);
  const [meta, setMeta] = useState<{ query: string; mode: "semantic" | "keyword"; total_projects: number }>({
    query: "",
    mode: "keyword",
    total_projects: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!query) {
      setResults([]);
      setMatchedTools([]);
      setMatchedCategories([]);
      setMeta({ query: "", mode: "keyword", total_projects: 0 });
      return;
    }

    let cancelled = false;
    setLoading(true);

    const params = new URLSearchParams({ q: query, limit: "30" });
    apiFetch<SearchResponse>(`/search?${params}`)
      .then((res) => {
        if (cancelled) return;
        setResults(res.data.projects);
        setMatchedTools(res.data.tools);
        setMatchedCategories(res.data.categories);
        setMeta(res.meta);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [query, page]);

  return { results, matchedTools, matchedCategories, meta, loading, error };
}
